<template>
	<component
		:is="link ? 'a' : 'button'"
		:href="link"
		:class="[
			`button button--${bgColor}`,
			{
				'button--small': isSmall,
				'no-icon': !iconSwitch
			}
		]"
		:target="linkCondition === true ? '_blank' : null"
		:rel="link ? 'noopener noreferrer' : null"
		:title="elementTitle"
		:role="elementRole"
		:aria-label="elementAriaLabel"
		:disabled="isDisabled"
		v-bind="$attrs"
		@click="$emit('button-click', $event)"
	>
		{{ text }}
		<span
			v-if="iconSwitch"
			:class="iconClass"
			:role="iconRole ? iconRole : null"
			:aria-label="iconAriaLabel"
			:aria-hidden="!iconAriaLabel ? 'true' : null"
		/>
	</component>
</template>

<script>
/**
 * This component:
 *
 * - displays a button as a choice of anchor element or a button with ability to switch to small size.
 *
 * - can either be an anchor `<a>` or button `<button>`.
 *
 * If a value is set for `link`, then the component will be an anchor. If no value is set for `link`, then the component will be a button and emit `button-click` event.
 */
export default {
	name: "Button",
	props: {
		/**
		 * Set `text` to the desired label that should appear on the button. The current default is `Download file`.
		 */
		text: {
			type: String,
			default: "Button"
		},

		/**
		 * Set `elementTitle` to the desired description of the button's or anchor link's function. This description is for accessibility. The current default is `Button description`.
		 */
		elementTitle: {
			type: String,
			default: "Button description"
		},

		/**
		 * Use if download should be an anchor (navigates to new page) instead of a button (performs an action)
		 */
		link: {
			type: String
		},

		/**
		 * For `<a>` element only. Set to true if you want a link to open on a new page.
		 */
		elementTarget: {
			type: Boolean,
			default: false
		},

		/**
		 * Background color of button. Possible values: black, red, or inverted. Inverted displays white button with black text and icon
		 *
		 * @values red, black, inverted, black-outline, white
		 */
		bgColor: {
			type: String,
			default: "black",
			validator: (value) => {
				return ["black", "red", "inverted", "black-outline", "white"].includes(
					value
				);
			}
		},

		/**
		 * Show or hide an icon switch.
		 */
		iconSwitch: {
			type: Boolean,
			default: true
		},

		/**
		 * Add a name of an icon if needed, otherwise leave it empty.
		 *
		 * @values down, left, right, up, bold-up, bold-down, arrow-down, arrow-up, download, return, share, person, check, plus, close, lock, star, menu, calendar, date, search, cart, chart, list, mail, phone, play, audio, video, document, documents, news, print, info, email, chat, factsheet, refresh, reset, info_outline, filter_list, warning, eye, eye-blocked, bin, arrow-up-right, clear, facebook, twitter, linkedin, rss, podcast
		 */
		icon: {
			type: String
		},

		/**
		 * Mini button switch
		 */
		isSmall: {
			type: Boolean,
			default: false
		},

		/**
		 * Disabled button state.
		 */
		isDisabled: {
			type: Boolean,
			default: false
		},

		/**
		 * Element role for A11Y.
		 */
		elementRole: {
			type: String,
			default: "button"
		},

		/**
		 * Element `aria-label` description
		 */
		elementAriaLabel: {
			type: String,
			default: "Button"
		},

		/**
		 * Describe what this icon should represent for accessibility (A11Y).
		 */
		iconRole: {
			type: String,
			default: "Icon"
		},

		/**
		 * Icon aria label A11Y description.
		 */
		iconAriaLabel: {
			type: String,
			default: "Icon"
		}
	},

	computed: {
		iconClass() {
			return `icon icon-${this.icon}`;
		},
		linkCondition() {
			let result = false;
			if (this.elementTarget === true && this.link.length > 0) {
				result = true;
			}
			return result;
		}
	}
};
</script>
